import { WebUrlSegments } from "@ms/uno-constants/lib/local/WebConstants";
import { ArgumentError } from "@ms/uno-errors/lib/local/errors";
/**
 * Checks if a pathname starts with a given prefix segment, requiring a path boundary
 * (either "/" after the prefix or exact match at end of string).
 * This prevents false positives like "/ui/web-admin" matching "/ui/web".
 * @param prefix Expected to be a path segment without a leading slash (e.g. "ui/web" or "webui").
 */ export function hasPathPrefix(pathname, prefix) {
    const normalizedPrefix = prefix.startsWith("/") ? prefix.slice(1) : prefix;
    const prefixWithSlash = `/${normalizedPrefix}`;
    const lower = pathname.toLowerCase();
    return lower === prefixWithSlash || lower.startsWith(`${prefixWithSlash}/`);
}
/**
 * Detects the active web URL prefix from the current pathname.
 * Checks for the new prefix first, then falls back to the legacy prefix.
 * Requires a path boundary after the prefix to avoid false positives.
 * @param pathname The browser pathname (e.g. window.location.pathname)
 * @returns The matching WebUrlSegments value
 */ export function detectActiveWebUrlPrefix(pathname) {
    return getAllWebUrlPrefixes().find((prefix)=>hasPathPrefix(pathname, prefix)) ?? WebUrlSegments.WebUI;
}
/**
 * Returns all recognized web URL prefixes, with the new prefix first.
 */ export function getAllWebUrlPrefixes() {
    return [
        WebUrlSegments.PlannerServiceWebPathSegment,
        WebUrlSegments.WebUI
    ];
}
/**
 * Normalizes a web URL prefix to one of the recognized entry points.
 * Falls back to the legacy WebUI prefix for unrecognized values.
 */ export function normalizeActiveWebUrlPrefix(prefix) {
    return getAllWebUrlPrefixes().includes(prefix) ? prefix : WebUrlSegments.WebUI;
}
/**
 * Validates that a prefix is one of the recognized web client entry points.
 * @throws {ArgumentError} When the prefix is not recognized.
 */ export function validateWebUrlPrefix(prefix) {
    if (!getAllWebUrlPrefixes().includes(prefix)) {
        throw new ArgumentError("prefix", `prefix ${prefix} must be one of: ${getAllWebUrlPrefixes().join(", ")}`);
    }
    return prefix;
}
/**
 * Returns whether a pathname targets any recognized web client entry point.
 * @param pathname The request or browser pathname (e.g. "/ui/web/plan/123")
 * @returns True if pathname matches any recognized web client entry point, false otherwise
 */ export function isWebClientPath(pathname) {
    return getAllWebUrlPrefixes().some((prefix)=>hasPathPrefix(pathname, prefix));
}
/**
 * Reconciles a ECS-provided MSAL redirect path with the active web URL prefix.
 * The ECS config may return paths with a different prefix (e.g. "webui/blankmsalredirect")
 * than the one detected from the current URL (e.g. "ui/web"). This function replaces
 * any known prefix in the path with the active prefix so redirect URIs match the entry point.
 * @param ecsPath The path from msalSettings (e.g. "webui/blankmsalredirect")
 * @param activePrefix The detected active web URL prefix (e.g. "ui/web")
 * @returns The reconciled path, or undefined if ecsPath is falsy
 */ export function reconcileMsalRedirectPath(ecsPath, activePrefix) {
    if (!ecsPath) {
        return undefined;
    }
    const validatedActivePrefix = validateWebUrlPrefix(activePrefix);
    const normalizedPath = ecsPath.startsWith("/") ? ecsPath.substring(1) : ecsPath;
    const matchedPrefix = getAllWebUrlPrefixes().find((prefix)=>normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`));
    if (matchedPrefix === undefined || matchedPrefix === validatedActivePrefix) {
        return normalizedPath;
    }
    return validatedActivePrefix + normalizedPath.substring(matchedPrefix.length);
}
